/**
 * ZOOM MIT DEN FINGERN, IN DER BILD- UND IN DER SEITENVORSCHAU.
 *
 * ORIGINALTEXT MARVIN (15.09.2026)
 * > „bei dem öffnen der fotos brauchen wir keinen Rahmen. Es reicht das reine
 * >  Foto, dann mit Funktion links/rechts wischen und Zoom mit den Fingern
 * >  fehlt noch. Bei den pdf Dateien fehlt auch der Zoom."
 *
 * ## DIE REGELN STEHEN HIER UND NICHT IM BAUTEIL
 *
 * DIESE DATEI TRÄGT KEIN JSX, damit ein Prüfstand sie WIRKLICH ausrechnen
 * kann. Wohin ein Bild rutscht, wenn zwei Finger es um den Faktor 2,4
 * aufziehen und der Punkt zwischen ihnen 40 Punkte über der Mitte liegt,
 * lässt sich nicht suchen, das muss man nachrechnen. Dieselbe Aufteilung wie
 * bei `wischen.ts`, `terminraster.ts`, `kartenebene.ts` und den übrigen
 * Regeldateien in `web/src/teile/`.
 *
 * ## WARUM ES AUCH HIER KEIN `preventDefault` IM JSX GIBT
 *
 * React hängt `touchstart`, `touchmove` und `wheel` PASSIV am Wurzelknoten
 * an, und ein passiver Zuhörer darf nicht abbrechen: der Ruf wird
 * stillschweigend übergangen, ohne Fehler und ohne Meldung in der Konsole.
 * Dieses Haus hat das am 02.09.2026 an der Tiefbaukarte bezahlt. Das Rad
 * hängt deshalb im Bauteil von Hand am Knoten (`{ passive: false }`), und die
 * Fingergesten stehen als ZUSAGE im Stylesheet (`touch-action`).
 *
 * ## DER WISCH UND DER ZOOM SCHLIESSEN SICH AUS
 *
 * **Solange gezoomt ist, blättert kein Wisch mehr.** Ohne diesen Riegel
 * wechselt jedes Verschieben eines vergrößerten Bildes die Datei, und zwar
 * genau dann, wenn jemand sehr genau hinsieht. Umgekehrt gilt dasselbe:
 * `darfWischen` ist die eine Stelle, an der beide Gesten voneinander wissen.
 *
 * ## WAS GEKLEMMT WIRD UND WARUM
 *
 * Ein vergrößertes Bild darf sich nur so weit schieben lassen, dass seine
 * Kante nicht in den Kasten hineinwandert. Ohne diesen Riegel schiebt ein
 * Finger das Foto aus dem Bild, und es steht eine leere Fläche da, die von
 * einem kaputten Fenster nicht zu unterscheiden ist. Beim Faktor 1 ist der
 * erlaubte Versatz 0: dann füllt das Bild den Kasten genau.
 */

/** Wie weit ein Bild höchstens aufgezogen wird. */
export const GROESSTER = 6;

/** Und wie weit es kleinstenfalls steht: gar nicht gezoomt. */
export const KLEINSTER = 1;

/**
 * Wohin ein Doppeltipp führt.
 *
 * 2,5 und nicht 2: bei 2 liest man eine Hausnummer auf einem Nachweisfoto
 * immer noch nicht, und wer zweimal tippen muss, tippt beim dritten Mal gar
 * nicht mehr.
 */
export const DOPPELTIPP = 2.5;

/** Wie schnell zwei Tipps aufeinanderfolgen müssen, in Millisekunden. */
export const DOPPELTIPP_MS = 320;

/**
 * Wie weit die beiden Tipps auseinanderliegen dürfen, in CSS-Punkten.
 *
 * 28 und nicht 4: ein Daumen trifft nichts genauer, und zwei Tipps, die als
 * zwei einzelne gelten, zoomen nie.
 */
export const DOPPELTIPP_PUNKTE = 28;

/**
 * Ab wann ein Bild als gezoomt gilt.
 *
 * Nicht `> 1`: eine Fingerbewegung endet fast nie auf genau 1, und ein Bild,
 * das mit dem Faktor 1,0003 dasteht, sieht aus wie ungezoomt und blättert
 * dann nicht mehr.
 */
export const GEZOOMT_AB = 1.02;

/** Wohin ein Rad-Ereignis führt: ein Schritt, in beide Richtungen gleich. */
export const RADSCHRITT = 1.18;

/** Was eine Zoomfläche mit sich trägt. */
export type Zoomstand = {
  /** 1 heißt „wie hineingelegt". */
  faktor: number;
  /** Versatz in CSS-Punkten, von der Mitte aus. */
  x: number;
  y: number;
};

/** Der Ausgangsstand, und zugleich das Ziel jedes Zurücksetzens. */
export const ZURUECK: Zoomstand = { faktor: 1, x: 0, y: 0 };

/** Ein Punkt auf dem Bildschirm, in CSS-Punkten. */
export type Punkt = { x: number; y: number };

/** Die Größe des Kastens, in dem gezoomt wird, in CSS-Punkten. */
export type Kasten = { breite: number; hoehe: number };

/** Steht dieses Bild vergrößert da? */
export function istGezoomt(s: Zoomstand): boolean {
  return s.faktor >= GEZOOMT_AB;
}

/**
 * Darf ein Wisch blättern?
 *
 * DIE EINE STELLE, AN DER SICH DIE BEIDEN GESTEN KENNEN. Sie steht hier und
 * nicht im Bauteil, damit ein Prüfstand sie ausrechnen kann.
 */
export function darfWischen(s: Zoomstand): boolean {
  return !istGezoomt(s);
}

/** Der Abstand zweier Finger, in CSS-Punkten. */
export function abstand(a: Punkt, b: Punkt): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/** Der Punkt zwischen zwei Fingern. */
export function mitte(a: Punkt, b: Punkt): Punkt {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/** Innerhalb der Grenzen, und nie darüber hinaus. */
export function faktorGeklemmt(f: number): number {
  if (!Number.isFinite(f)) return KLEINSTER;
  return Math.min(GROESSTER, Math.max(KLEINSTER, f));
}

/**
 * Den Versatz so weit zurückholen, dass keine Kante hereinwandert.
 *
 * Bei `transform-origin: center` deckt ein um `f` vergrößerter Inhalt den
 * Kasten so weit über, dass auf jeder Seite `(breite * f - breite) / 2` Punkte
 * Luft sind. Genau so weit darf verschoben werden und keinen Punkt weiter.
 */
export function geklemmt(s: Zoomstand, k: Kasten): Zoomstand {
  const f = faktorGeklemmt(s.faktor);
  const luftX = Math.max(0, (k.breite * f - k.breite) / 2);
  const luftY = Math.max(0, (k.hoehe * f - k.hoehe) / 2);
  const x = Number.isFinite(s.x) ? s.x : 0;
  const y = Number.isFinite(s.y) ? s.y : 0;
  return {
    faktor: f,
    x: Math.min(luftX, Math.max(-luftX, x)),
    y: Math.min(luftY, Math.max(-luftY, y)),
  };
}

/**
 * Auf einen neuen Faktor gehen, UM EINEN PUNKT HERUM.
 *
 * DAS IST DIE GANZE RECHNUNG DIESER DATEI. Was unter den Fingern liegt, muss
 * unter den Fingern bleiben: wer um die Mitte zoomt, während der Finger am
 * Rand liegt, zieht dem Benutzer die Stelle weg, die er gerade ansieht, und
 * er sucht sie danach mit dem zweiten Griff.
 *
 * `p` ist der Punkt IM KASTEN (0,0 ist seine linke obere Ecke).
 */
export function zoomAn(
  s: Zoomstand, neuerFaktor: number, p: Punkt, k: Kasten,
): Zoomstand {
  const alt = faktorGeklemmt(s.faktor);
  const neu = faktorGeklemmt(neuerFaktor);
  /* Von der Mitte aus gerechnet, denn `transform-origin` steht auf `center`.
     Wer hier von der Ecke aus rechnet, bekommt eine Verschiebung, die mit dem
     Faktor wächst, und das Bild springt beim Zoomen quer über den Kasten. */
  const mx = p.x - k.breite / 2;
  const my = p.y - k.hoehe / 2;
  const v = neu / alt;
  return geklemmt({
    faktor: neu,
    x: mx - (mx - s.x) * v,
    y: my - (my - s.y) * v,
  }, k);
}

/** Verschieben, und nie über die Kante hinaus. */
export function schieben(
  s: Zoomstand, dx: number, dy: number, k: Kasten,
): Zoomstand {
  return geklemmt({ faktor: s.faktor, x: s.x + dx, y: s.y + dy }, k);
}

/**
 * Ein Rad-Ereignis am Schreibtisch.
 *
 * Der Zeiger hat keine zwei Finger, und ein Bild, das sich am Schreibtisch
 * nicht vergrößern lässt, ist für den Innendienst genauso wenig zu lesen wie
 * auf der Baustelle. Nach oben gedreht heißt näher.
 */
export function zoomRad(
  s: Zoomstand, deltaY: number, p: Punkt, k: Kasten,
): Zoomstand {
  const ziel = deltaY < 0 ? s.faktor * RADSCHRITT : s.faktor / RADSCHRITT;
  return zoomAn(s, ziel, p, k);
}

/**
 * Ein Doppeltipp schaltet um und rechnet nichts aus.
 *
 * Gezoomt: zurück auf 1. Nicht gezoomt: auf `DOPPELTIPP`, um die getippte
 * Stelle herum. **Ein Doppeltipp, der weiter hineinzoomt, hat keinen Weg
 * zurück** - und auf einem Telefon ist das Zurücksetzen mit zwei Fingern
 * genau die Bewegung, die man vermeiden wollte.
 */
export function doppeltipp(s: Zoomstand, p: Punkt, k: Kasten): Zoomstand {
  if (istGezoomt(s)) return { ...ZURUECK };
  return zoomAn(s, DOPPELTIPP, p, k);
}

/**
 * Ist das ein Doppeltipp, oder sind es zwei einzelne?
 *
 * Beides gemessen und keines geraten: zu lange her, oder zu weit weg, heißt
 * zwei einzelne Tipps. Ohne die zweite Bedingung zoomt jedes zweite Antippen
 * eines Wischpfeils das Bild auf.
 */
export function istDoppeltipp(
  vorher: { zeit: number; p: Punkt } | null, jetzt: { zeit: number; p: Punkt },
): boolean {
  if (!vorher) return false;
  if (jetzt.zeit - vorher.zeit > DOPPELTIPP_MS) return false;
  return abstand(vorher.p, jetzt.p) <= DOPPELTIPP_PUNKTE;
}

/**
 * Wie der Stand in eine CSS-Umformung kommt.
 *
 * DIE REIHENFOLGE IST NICHT BELIEBIG: erst schieben, dann skalieren. Wer
 * andersherum schreibt, multipliziert den Versatz mit dem Faktor, und das
 * Bild springt beim Zoomen aus dem Kasten.
 */
export function alsUmformung(s: Zoomstand): string {
  return `translate(${s.x}px, ${s.y}px) scale(${s.faktor})`;
}

/**
 * Was unter dem Zoomknopf steht.
 *
 * Eine Zahl ohne Einheit ist keine Auskunft; „100 %" sagt, dass man am
 * Ausgangspunkt steht, und genau das ist die Frage, die jemand vor einem
 * halb aufgezogenen Plan hat.
 */
export function alsProzent(s: Zoomstand): string {
  return Math.round(s.faktor * 100) + ' %';
}
